import { useTranslation } from "react-i18next";
import { Input, Skeleton } from "@/shared/ui";
import { EmptyState } from "@/shared/components";
import { formatMoneyKzt, formatDateForDisplay, type Locale } from "@/shared/utils";
import { useGetAvailableExternalIncomesQuery } from "@/features/accounting/income";
import type { ProjectIncomeSource } from "../types";

interface Props {
  sources: ProjectIncomeSource[];
  onChange: (sources: ProjectIncomeSource[]) => void;
  error?: string;
}

function cleanMoney(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
}

export default function IncomeSourcesTable({ sources, onChange, error }: Props) {
  const { t, i18n } = useTranslation("ProjectsPage");
  const locale = (i18n.language as Locale) || "ru";
  const { data: incomes, isLoading, isFetching } = useGetAvailableExternalIncomesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  function getAmount(incomeId: number): string {
    return sources.find((s) => s.income_id === incomeId)?.amount ?? "";
  }

  function handleAmountChange(incomeId: number, rawValue: string, availableAmount: string) {
    const cleaned = cleanMoney(rawValue);
    const available = parseFloat(availableAmount) || 0;
    const entered = parseFloat(cleaned) || 0;
    const capped = entered > available ? availableAmount : cleaned;

    const next = sources.filter((s) => s.income_id !== incomeId);
    if (capped !== "" && parseFloat(capped) > 0) {
      next.push({ income_id: incomeId, amount: capped });
    }
    onChange(next);
  }

  const reservedTotal = sources.reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton height={40} />
        <Skeleton height={40} />
      </div>
    );
  }

  if (!incomes || incomes.length === 0) {
    return <EmptyState compact noDataText={t("projectForm.sourcesEmpty")} />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 pb-2 border-b surface-base-stroke">
        <span className="flex-1 text-body-regular-xs content-base-secondary">{t("projectForm.sourcesTable.source")}</span>
        <span className="w-32 text-right text-body-regular-xs content-base-secondary">
          {t("projectForm.sourcesTable.available")}
        </span>
        <span className="w-40 text-right text-body-regular-xs content-base-secondary">
          {t("projectForm.sourcesTable.reserve")}
        </span>
      </div>

      {incomes.map((income) => (
        <div key={income.id} className="flex items-center gap-3 py-2.5 border-b surface-base-stroke">
          <div className="flex-1 min-w-0">
            <p className="text-body-regular-sm content-base-primary truncate">{income.source_name}</p>
            <p className="text-body-regular-xs content-base-secondary truncate">
              {formatDateForDisplay(income.document_date)}
            </p>
          </div>
          <span className="w-32 flex justify-end text-body-regular-sm content-base-secondary">
            {isFetching ? (
              <Skeleton width={70} height={16} />
            ) : (
              formatMoneyKzt(income.available_amount, locale, false)
            )}
          </span>
          <div className="w-40">
            <Input
              isSmall
              placeholder="0"
              className="text-right"
              value={getAmount(income.id)}
              onChange={(e) => handleAmountChange(income.id, e.target.value, income.available_amount)}
            />
          </div>
        </div>
      ))}

      <div className="mt-3 flex items-center justify-between p-3 radius-md surface-secondary-fill">
        <span className="text-body-bold-sm content-base-primary">{t("projectForm.sourcesTable.reservedTotal")}</span>
        <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(reservedTotal, locale, false)}</span>
      </div>

      {error && <p className="mt-2 text-body-regular-sm content-action-negative">{error}</p>}
    </div>
  );
}
